import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Container,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import api from "../../services/api";
import { BASE_URL } from "../../services/configUrls";

const TpoEvent = ({ disableContainer = false, height, plainTable = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTpoRankings = async () => {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem("accessToken");
        const response = await api.get(`${BASE_URL}/admin/tpo_ranking`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });
        
        let rankingsData = [];
        if (response.data) {
          if (response.data.rankings) {
            rankingsData = Array.isArray(response.data.rankings)
              ? response.data.rankings
              : [response.data.rankings];
          } else if (Array.isArray(response.data)) {
            rankingsData = response.data;
          } else if (response.data.year || response.data.placed_count !== undefined) {
            rankingsData = [response.data];
          } else if (typeof response.data === "object" && response.data !== null) {
            rankingsData = Object.entries(response.data).map(([year, details]) => ({
              year,
              ...details,
            }));
          }
        }
        setData(rankingsData);
      } catch (error) {
        console.error("Error fetching TPO rankings:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTpoRankings();
  }, []);


  const tableContent = (
    <TableContainer sx={{ flex: 1, overflowY: "auto", width: "100%" }}>
      <Table sx={{ width: "100%" }} aria-label="placement ranking table">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#fcfaff" }}>
            <TableCell
              sx={{
                fontWeight: 700,
                color: "#475569",
                fontSize: "14px",
                borderBottom: "1px solid #f1f5f9",
                py: 1.5,
                pl: 2.5,
              }}
            >
              Year
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                color: "#475569",
                fontSize: "14px",
                borderBottom: "1px solid #f1f5f9",
                py: 1.5,
              }}
            >
              Rank
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                color: "#475569",
                fontSize: "14px",
                borderBottom: "1px solid #f1f5f9",
                py: 1.5,
              }}
            >
              <Box>
                Students
                <Typography component="div" sx={{ fontSize: "9px", fontWeight: 500, color: "#64748b", mt: 0.25 }}>
                Students have completed at least one internship
                </Typography>
              </Box>
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                color: "#475569",
                fontSize: "14px",
                borderBottom: "1px solid #f1f5f9",
                py: 1.5,
                pr: 2.5,
              }}
            >
              Placed
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} sx={{ color: "#8b5cf6" }} />
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4, color: "#64748b" }}>
                Minimum limit not reached
              </TableCell>
            </TableRow>
          ) : (
            data.map((row, idx) => {
              const placed = Number(row.placed_count !== undefined ? row.placed_count : (row.placed !== undefined ? row.placed : 0));
              const limit = Number(row.limit !== undefined ? row.limit : 0);
              const isLimitMet = placed > limit;

              return (
                <TableRow
                  key={idx}
                  sx={{
                    "&:hover": {
                      backgroundColor: "#fdfbff",
                    },
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "#1e293b",
                      borderBottom: idx === data.length - 1 ? "none" : "1px solid #f1f5f9",
                      py: 1.5,
                      pl: 2.5,
                    }}
                  >
                    {row.year}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: idx === data.length - 1 ? "none" : "1px solid #f1f5f9",
                      py: 1.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isLimitMet && row.rank !== null && row.rank !== undefined ? (
                        <Chip
                          label={`#${row.rank}`}
                          size="small"
                          sx={{
                            backgroundColor: "#fef3c7",
                            color: "#d97706",
                            fontWeight: 700,
                            borderRadius: "6px",
                            fontSize: "13px",
                            px: 0.5,
                          }}
                        />
                      ) : (
                        <Tooltip title={`Minimum limit not reached (${limit})`} arrow>
                          <Box sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}>
                            <InfoOutlined sx={{ color: "#f59e0b", fontSize: 20 }} />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: 500,
                      color: "#475569",
                      borderBottom: idx === data.length - 1 ? "none" : "1px solid #f1f5f9",
                      py: 1.5,
                    }}
                  >
                    {row.eligible_count !== undefined ? row.eligible_count : row.total}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "#10b981",
                      borderBottom: idx === data.length - 1 ? "none" : "1px solid #f1f5f9",
                      py: 1.5,
                      pr: 2.5,
                    }}
                  >
                    {row.placed_count !== undefined ? row.placed_count : row.placed}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (plainTable) {
    return tableContent;
  }

  const paperWrapper = (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #ebd9fc",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 20px rgba(139, 92, 246, 0.05)",
        height: height || "auto",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/* Top Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          backgroundColor: "#f5ecfe",
          borderBottom: "1px solid #ebd9fc",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FiberManualRecordIcon sx={{ color: "#8b5cf6", fontSize: 14 }} />
          <Typography variant="h6" fontWeight={700} color="#1e1b4b" sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            National Placement Leadership Ranking
            <EmojiEventsIcon sx={{ color: "#d97706", fontSize: 22 }} />
          </Typography>
        </Box>
      </Box>
      {tableContent}
    </Paper>
  );

  if (disableContainer) {
    return paperWrapper;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {paperWrapper}
    </Container>
  );
};

export default TpoEvent;
